import type { GdalFieldInfo } from './gdal.types'

export type FilterableType = 'numeric' | 'date' | 'text'
export type TypeCategory = 'string' | 'numeric' | 'date' | 'other'

export type ElementFieldConfig = {
  fieldName: string
  gdalType: string
  ignore: boolean
  key: string
  // String
  titleCase: boolean
  searchData: boolean
  // Numeric
  toInt: boolean
  elseAsNone: boolean
  // Date
  fromEpoch: boolean
  // Shared
  vulnerabilityCharacteristics: string
  filterableEnabled: boolean
  filterableType: FilterableType | ''
  filterableLabel: string
  reportEnabled: boolean
  reportAlias: string
  reportConfidence: 'A' | 'B' | 'C' | 'D' | 'E' | ''
  // Value mapping
  replaceWithNone: string
  renameValues: string
  // Top-level config
  sentenceExclusion?: boolean
}

export function getTypeCategory(gdalType: string): TypeCategory {
  const t = gdalType.toLowerCase()
  if (t.includes('string') || t.includes('wide')) return 'string'
  if (
    t.includes('integer') ||
    t.includes('real') ||
    t.includes('float') ||
    t.includes('double') ||
    t.includes('numeric')
  )
    return 'numeric'
  if (t.includes('date') || t.includes('time')) return 'date'
  return 'other'
}

export function createDefaultElementFieldConfig(field: GdalFieldInfo): ElementFieldConfig {
  const category = getTypeCategory(field.type)
  const filterableType: FilterableType | '' =
    category === 'numeric' ? 'numeric' : category === 'date' ? 'date' : 'text'
  return {
    fieldName: field.name,
    gdalType: field.type,
    ignore: false,
    key: field.name,
    titleCase: false,
    searchData: false,
    toInt: false,
    elseAsNone: false,
    fromEpoch: false,
    vulnerabilityCharacteristics: '',
    filterableEnabled: false,
    filterableType,
    filterableLabel: field.name,
    reportEnabled: false,
    reportAlias: '',
    reportConfidence: '',
    replaceWithNone: '',
    renameValues: '',
    sentenceExclusion: false,
  }
}

export function buildElementConfig(fields: ElementFieldConfig[]): Record<string, unknown> {
  const fieldEntries: Record<string, unknown> = {}

  for (const field of fields) {
    if (field.ignore) continue

    const opts: Record<string, unknown> = {}

    if (field.titleCase) opts.title_case = true
    if (field.searchData) opts.search_data = true
    if (field.toInt) opts.to_int = true
    if (field.elseAsNone) opts.else_as_none = true
    if (field.fromEpoch) opts.from_epoch = true

    if (field.vulnerabilityCharacteristics.trim()) {
      opts.vulnerability_characteristics = field.vulnerabilityCharacteristics.trim()
    }

    if (field.filterableEnabled && field.filterableType) {
      opts.filterable_information = {
        type: field.filterableType,
        label: field.filterableLabel.trim() || field.fieldName,
      }
    }

    if (field.reportEnabled) {
      opts.report_fields = {
        alias: field.reportAlias.trim() || field.fieldName,
        confidence: field.reportConfidence || null,
      }
    }

    if (field.replaceWithNone.trim()) {
      const values = field.replaceWithNone
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean)
      opts.replace_with_none = values.map((v) => {
        const n = Number(v)
        return !isNaN(n) ? n : v
      })
    }

    if (field.renameValues.trim()) {
      const pairs: Record<string, string> = {}
      field.renameValues.split(',').forEach((pair) => {
        const idx = pair.indexOf(':')
        if (idx > -1) {
          const k = pair.slice(0, idx).trim()
          const v = pair.slice(idx + 1).trim()
          if (k && v) pairs[k] = v
        }
      })
      if (Object.keys(pairs).length > 0) opts.rename_values = pairs
    }

    const outputKey = field.key.trim() || field.fieldName
    const keyDiffers = outputKey !== field.fieldName
    const hasOpts = Object.keys(opts).length > 0

    if (!hasOpts) {
      // Identity or simple rename: { "Output Name": "source_field" }
      fieldEntries[outputKey] = field.fieldName
    } else {
      if (keyDiffers) opts.key = field.fieldName
      fieldEntries[outputKey] = opts
    }
  }

  const sentenceExclusions = fields
    .filter((f) => !f.ignore && f.sentenceExclusion)
    .map((f) => f.key.trim() || f.fieldName)

  const output: Record<string, unknown> = {}
  if (sentenceExclusions.length > 0) output.sentence_exclusions = sentenceExclusions
  output.fields = fieldEntries
  return output
}
